// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, Input, OnInit } from '@angular/core';
import { downloadCSVFile, getFormattedDate } from '@lfx/core/helper';
import {
  EmployeeEventsAttendedStats,
  EmployeeEventsSpokenStats,
  Company
} from '@lfx/core/models';
import { CompanyService } from '@lfx/core/services';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'lfx-profile-snapshot-employee-events',
  templateUrl: './profile-snapshot-employee-events.component.html',
  styleUrls: ['./profile-snapshot-employee-events.component.scss'],
  standalone: false,
})
export class ProfileSnapshotEmployeeEventsComponent implements OnInit {
  @Input() timeRange$: Observable<string>;
  @Input() userId: string;
  @Input() projectId?: string;
  searchValue = '';
  _searchValue = new BehaviorSubject<string>('');
  searchValue$ = this._searchValue.asObservable();
  attendedCollectionSize = 0;
  attendedCurrentPage = 1;
  attendedPageSize = 10;
  _attendedPage = new BehaviorSubject<number>(1);
  attendedPage$ = this._attendedPage.asObservable();
  attendedData$: Observable<any[]> = of([]);
  attendedPageData$: Observable<any[]>;
  isAttendedLoading = true;
  spokenCollectionSize = 0;
  spokenCurrentPage = 1;
  spokenPageSize = 10;
  _spokenPage = new BehaviorSubject<number>(1);
  spokenPage$ = this._spokenPage.asObservable();
  spokenData$: Observable<any[]> = of([]);
  spokenPageData$: Observable<any[]>;
  isSpokenLoading = true;
  myCompany$: Observable<Company>;
  activeTab = 'Attended';
  attendedColumns = [
    {
      name: 'Event Name',
      prop: 'eventName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Date',
      prop: 'eventStartDate',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  spokenColumns = [
    {
      name: 'Event Name',
      prop: 'eventName',
      clickableText: false,
      linkableText: false,
      dataStyle: {
        fontWeight: '400',
        fontSize: '14px',
        // color: '#009ADE',
      },
    },
    {
      name: 'Date',
      prop: 'eventStartDate',
      clickableText: false,
      // sortable: true,
      sortDir: 'desc',
      dataStyle: {
        // color: '#009ADE',
      },
    },
  ];

  constructor(private companyService: CompanyService) {
    this.myCompany$ = this.companyService.getMyCompany();
  }

  ngOnInit(): void {
    this.attendedData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._attendedPage.next(1);
          this.attendedCurrentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isAttendedLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeEventsAttended(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange,
            this.projectId
          )
          .pipe(
            map(data => this.transformAttendedData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.attendedCollectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isAttendedLoading = false))
    );

    this.attendedPageData$ = combineLatest([
      this.attendedData$,
      this.attendedPage$,
    ]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.attendedPageSize;
        const endIndex = startIndex + this.attendedPageSize;

        return data.slice(startIndex, endIndex);
      })
    );
    this.spokenData$ = combineLatest([
      this.myCompany$,
      this.timeRange$.pipe(
        tap(() => {
          this._spokenPage.next(1);
          this.spokenCurrentPage = 1;
        })
      ),
      this.searchValue$,
    ]).pipe(
      tap(() => {
        this.isSpokenLoading = true;
      }),
      switchMap(([company, timeRange, searchValue]) =>
        this.companyService
          .getEmployeeEventsSpoken(
            this.userId,
            company.id,
            this.companyService.isAllOrgsRollup(),
            timeRange,
            this.projectId
          )
          .pipe(
            map(data => this.transformSpokenData(data)),
            map(data => this.filterData(data, searchValue)),
            tap(data => {
              this.spokenCollectionSize = data.length;
            })
          )
      ),
      tap(() => (this.isSpokenLoading = false))
    );

    this.spokenPageData$ = combineLatest([
      this.spokenData$,
      this.spokenPage$,
    ]).pipe(
      map(([data, page]) => {
        const startIndex = (page - 1) * this.spokenPageSize;
        const endIndex = startIndex + this.spokenPageSize;

        return data.slice(startIndex, endIndex);
      })
    );
  }

  private transformAttendedData(data: EmployeeEventsAttendedStats[]): any[] {
    return data.map(item => ({
      eventName: this.formatText(item.eventName),
      eventStartDate: this.formatDate(item.eventStartDate),
    }));
  }

  private transformSpokenData(data: EmployeeEventsSpokenStats[]): any[] {
    return data.map(item => ({
      eventName: this.formatText(item.eventName),
      eventStartDate: this.formatDate(item.eventStartDate),
    }));
  }

  formatDate(dateString: string) {
    const formattedDate = getFormattedDate(dateString);

    return this.formatText(formattedDate);
  }

  formatText(text, subtext = null, url = null) {
    return {
      text,
      subtext,
      url,
    };
  }

  onAttendedPageChange(event: any) {
    this._attendedPage.next(event);
  }

  onSpokenPageChange(event: any) {
    this._spokenPage.next(event);
  }

  private filterData(data: any[], searchValue: string): any[] {
    return data.filter(i =>
      i.eventName.text.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  clearSearch() {
    this.searchValue = '';
    this.searchInputChange('');
  }

  searchInputChange(value: string): void {
    this._attendedPage.next(1);
    this.attendedCurrentPage = 1;
    this._spokenPage.next(1);
    this.spokenCurrentPage = 1;
    this._searchValue.next(value);
  }

  exportAttendedData() {
    const subscription = this.attendedData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Event Name': item.eventName.text,
          'Event Start Date': item.eventStartDate.text,
        })),
        'Employee Events Attended'
      );
      subscription.unsubscribe();
    });
  }

  exportSpokenData() {
    const subscription = this.spokenData$.subscribe(data => {
      downloadCSVFile(
        data.map(item => ({
          'Event Name': item.eventName.text,
          'Event Start Date': item.eventStartDate.text,
        })),
        'Employee Events Spoken At'
      );
      subscription.unsubscribe();
    });
  }

  // tabChange(newTabID, shouldRoute = true) {
  //   console.log('newTabID', newTabID, 'shouldRoute', shouldRoute);
  // }
}
